/**
 * P2 · Feb 2026 · Sticky "Latest Row" flash.
 *
 * `useLatestRowFlash(rows, idKey, durationMs=3000)`
 *   Tracks which row ids appeared between renders and returns a live Set of
 *   ids that should be visually highlighted for the given duration. Each new
 *   id gets its own 3-second timer, so a burst of saves stays highlighted
 *   for a full window even if the grid re-fetches mid-flash.
 *
 * Zero-config for callers — pass the freshly-rendered rows and the primary
 * key ("id" by default). First render never flashes (baseline).
 *
 * Usage:
 *   const flashing = useLatestRowFlash(rows, "id");
 *   <tr className={flashing.has(row.id) ? "row-flash-emerald" : ""} />
 *
 * Companion CSS class `row-flash-emerald` lives in the global sheet.
 */
import { useEffect, useRef, useState } from "react";

export function useLatestRowFlash(rows, idKey = "id", durationMs = 3000) {
  const seenRef = useRef(null); // null on first render = baseline pass
  const [flashing, setFlashing] = useState(() => new Set());

  useEffect(() => {
    if (!Array.isArray(rows)) return;
    const currentIds = new Set(rows.map(r => r?.[idKey]).filter(Boolean));

    if (seenRef.current === null) {
      // First render — everything we see is baseline, nothing flashes.
      seenRef.current = currentIds;
      return;
    }

    const newIds = [];
    currentIds.forEach(id => { if (!seenRef.current.has(id)) newIds.push(id); });
    seenRef.current = currentIds;

    if (newIds.length === 0) return;

    setFlashing(prev => {
      const next = new Set(prev);
      newIds.forEach(id => next.add(id));
      return next;
    });

    const timers = newIds.map(id => setTimeout(() => {
      setFlashing(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, durationMs));

    return () => timers.forEach(clearTimeout);
  }, [rows, idKey, durationMs]);

  return flashing;
}
